// Lightweight, namespaced localStorage helpers. No accounts — progress lives
// only in the browser so a returning user resumes where they left off.

const NS = "pib";

function key(...parts) {
  return [NS, ...parts].join(".");
}

function read(k, fallback) {
  try {
    const raw = localStorage.getItem(k);
    return raw == null ? fallback : JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
}

function write(k, value) {
  try {
    localStorage.setItem(k, JSON.stringify(value));
    // notify same-tab listeners
    window.dispatchEvent(new CustomEvent("pib-storage", { detail: { key: k } }));
  } catch (e) {
    /* ignore quota / disabled storage */
  }
}

// ---- Role -----------------------------------------------------------------
export const ROLE_KEY = key("role");
export function getRole() {
  return read(ROLE_KEY, null);
}
export function setRole(role) {
  write(ROLE_KEY, role);
}

// ---- Ambassador phase checklists -----------------------------------------
// Stored as { [index]: true } per phase.
function checklistKey(slug, mode, phaseKey) {
  return key("checklist", slug, mode, phaseKey);
}
export function getChecklist(slug, mode, phaseKey) {
  return read(checklistKey(slug, mode, phaseKey), {});
}
export function setChecklistItem(slug, mode, phaseKey, index, checked) {
  const current = getChecklist(slug, mode, phaseKey);
  const next = { ...current };
  if (checked) next[index] = true;
  else delete next[index];
  write(checklistKey(slug, mode, phaseKey), next);
  return next;
}

// ---- Visited phases (for phases without checklists) -----------------------
function visitedKey(slug, mode) {
  return key("visited", slug, mode);
}
export function getVisited(slug, mode) {
  return read(visitedKey(slug, mode), {});
}
export function markVisited(slug, mode, phaseKey) {
  const current = getVisited(slug, mode);
  if (current[phaseKey]) return current;
  const next = { ...current, [phaseKey]: true };
  write(visitedKey(slug, mode), next);
  return next;
}

// ---- Volunteer flow -------------------------------------------------------
function volunteerKey(slug) {
  return key("volunteer", slug);
}
export function getVolunteerProgress(slug) {
  return read(volunteerKey(slug), { stepIndex: 0, done: {}, gathered: {}, quality: {}, kitsDone: 0, phase: "packing" });
}
export function setVolunteerProgress(slug, progress) {
  write(volunteerKey(slug), progress);
}

// ---- Reset ----------------------------------------------------------------
export function resetActivityMode(slug, mode) {
  const prefix = key("checklist", slug, mode);
  const visited = visitedKey(slug, mode);
  Object.keys(localStorage)
    .filter((k) => k.startsWith(prefix) || k === visited)
    .forEach((k) => localStorage.removeItem(k));
  window.dispatchEvent(new CustomEvent("pib-storage", { detail: { key: prefix } }));
}
export function resetVolunteer(slug) {
  localStorage.removeItem(volunteerKey(slug));
  window.dispatchEvent(new CustomEvent("pib-storage", { detail: { key: volunteerKey(slug) } }));
}

// ---- Kits packed (client-side, per-activity, all-time until reset) ---------
function kitsPackedKey(slug) {
  return key("kitsPacked", slug);
}
export function getKitsPacked(slug) {
  return read(kitsPackedKey(slug), 0) || 0;
}
export function incrementKitsPacked(slug) {
  const next = getKitsPacked(slug) + 1;
  write(kitsPackedKey(slug), next);
  return next;
}
export function resetKitsPacked(slug) {
  localStorage.removeItem(kitsPackedKey(slug));
}
